import type { SupportCaseDetail, SupportCaseEntry } from "@wallyo/contracts/console/cases";
import type { FormEvent } from "react";
import { featureName, isolate, t, type MessageId } from "../../i18n";
import { customerHref } from "../customers/customer-route";
import { CASE_FEATURES, type CaseControls } from "./case-access";
import {
  CHANNEL_ICONS,
  CHANNEL_LABELS,
  PRIORITY_LABELS,
  SMS_STATUS,
  STATUS_LABELS,
  STATUS_TONES,
  customerLine,
  deadlinePill,
  entryClass,
  entryHeading,
  entryTime,
  ownerLabel,
} from "./case-format";
import type { CaseFailure } from "./case-writes";
import { AccessRequestPanel } from "./AccessRequestPanel";

export type ComposerMode = "reply" | "note" | "call";

export type ComposerState = {
  mode: ComposerMode;
  body: string;
  /** A call's length in minutes, as typed. */
  minutes: string;
  problem: string | null;
  failure: CaseFailure | null;
  pending: boolean;
};

export type ComposerEvent =
  | { type: "mode"; mode: ComposerMode }
  | { type: "body"; body: string }
  | { type: "minutes"; minutes: string }
  | { type: "submit" };

export type CaseDialogKind = "reassign" | "close" | "link";

export type CasePanelViewProps = {
  detail: SupportCaseDetail;
  controls: CaseControls;
  /** A reply goes in the app: the case needs a customer account. */
  canReplyInApp: boolean;
  composer: ComposerState;
  now: Date;
  /** The claim (a one-click write): in flight, or what refused it. */
  claiming: boolean;
  claimFailure: CaseFailure | null;
  onClaim: () => void;
  onDialog: (kind: CaseDialogKind) => void;
  onComposer: (event: ComposerEvent) => void;
  onReload: () => void;
};

/** A failed write: what happened by its code, the request ID, and a reload when the case changed. */
export function CaseFailureNotice({ failure, onReload }: { failure: CaseFailure; onReload?: () => void }) {
  return (
    <div className="error" role="alert">
      <span>{failure.message}</span>
      <span className="ref">{failure.reference}</span>
      {failure.stale && onReload ? (
        <div>
          <button className="btn" type="button" onClick={onReload}>
            {t("cases.reload")}
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** One case: who and what, its timeline, and what this staff member may do with it. */
export function CasePanelView(props: CasePanelViewProps) {
  const { detail, controls, now } = props;
  const pill = deadlinePill(detail, now);
  return (
    <section className="card case-panel" aria-labelledby="case-title" data-case-panel={detail.supportCaseId}>
      <div className="top case-head">
        <div>
          <h3 id="case-title">
            <span className="mono case-ref">{detail.reference}</span> · <bdi>{detail.subject}</bdi>
          </h3>
          <p className="sub case-sub">
            {controls.customerPage && detail.customer ? (
              <a className="subject-link" href={customerHref(detail.customer.userId)}>
                <bdi>{customerLine(detail)}</bdi>
              </a>
            ) : (
              <bdi>{customerLine(detail)}</bdi>
            )}
            {" · "}
            {ownerLabel(detail)}
          </p>
          <div className="chips case-tags">
            <span className={`pill ${STATUS_TONES[detail.status]}`}>{t(STATUS_LABELS[detail.status])}</span>
            <span className={`pill ${pill.tone}`} data-deadline={pill.state}>
              {pill.label}
            </span>
            {/* An access case came in from nobody: there is no channel to name. */}
            {detail.channel === null ? (
              <span className="chip">{t("cases.kind.access")}</span>
            ) : (
              <span className="chip">
                <span aria-hidden="true">{CHANNEL_ICONS[detail.channel]}</span> {t(CHANNEL_LABELS[detail.channel])}
              </span>
            )}
            <span className="chip">{t("cases.priority", { priority: t(PRIORITY_LABELS[detail.priority]) })}</span>
          </div>
        </div>
        <CaseActions {...props} />
      </div>
      {/* An access case leads with the ask and the decision: that is the whole
          case, and the timeline below is how it got here. */}
      <AccessRequestPanel detail={detail} onDecided={props.onReload} />
      {props.claimFailure ? <CaseFailureNotice failure={props.claimFailure} onReload={props.onReload} /> : null}
      {detail.status === "closed" && detail.closeReason ? (
        <p className="foot">{t("cases.closedBecause", { reason: detail.closeReason })}</p>
      ) : null}
      <CaseTimeline entries={detail.entries} now={now} />
      <Composer {...props} />
    </section>
  );
}

function CaseActions({ detail, controls, claiming, onClaim, onDialog }: CasePanelViewProps) {
  return (
    <div className="btns case-actions">
      {controls.claim ? (
        <button className="btn primary" type="button" onClick={onClaim} disabled={claiming}>
          {claiming ? t("cases.sending") : t("cases.action.claim")}
        </button>
      ) : null}
      {controls.reassign ? (
        <button className="btn" type="button" onClick={() => onDialog("reassign")}>
          {t("cases.action.reassign")}
        </button>
      ) : detail.status !== "closed" ? (
        <button
          className="btn"
          type="button"
          disabled
          title={t("cases.needs", { feature: featureName(CASE_FEATURES.reassign) })}
          data-locked="reassign"
        >
          {t("cases.action.reassign")}
        </button>
      ) : null}
      {controls.link ? (
        <button className="btn" type="button" onClick={() => onDialog("link")}>
          {detail.customer ? t("cases.action.relink") : t("cases.action.link")}
        </button>
      ) : null}
      {controls.close ? (
        <button className="btn danger" type="button" onClick={() => onDialog("close")}>
          {t("cases.action.close")}
        </button>
      ) : null}
    </div>
  );
}

export function CaseTimeline({ entries, now }: { entries: readonly SupportCaseEntry[]; now: Date }) {
  if (entries.length === 0) return <div className="empty">{t("cases.timeline.empty")}</div>;
  return (
    <ol className="timeline case-timeline" aria-label={t("cases.timeline.aria")}>
      {entries.map((entry) => (
        <TimelineEntry key={entry.supportCaseEntryId} entry={entry} now={now} />
      ))}
    </ol>
  );
}

function TimelineEntry({ entry, now }: { entry: SupportCaseEntry; now: Date }) {
  const head = [entryTime(entry.createdAt, now), entryHeading(entry), entry.author.name || null]
    .filter((part): part is string => Boolean(part))
    .map(isolate)
    .join(" · ");
  const sms = entry.sms ? SMS_STATUS[entry.sms.status] : null;
  return (
    <li className={entryClass(entry)} data-entry={entry.supportCaseEntryId} data-visibility={entry.visibility}>
      <div className="when">
        <span>{head}</span>
        {entry.visibility === "internal" ? <span className="pill internal-tag">{t("cases.internal")}</span> : null}
        {sms ? (
          <span className={`pill ${sms.tone}`} data-sms={entry.sms?.status}>
            {t(sms.label)}
          </span>
        ) : null}
      </div>
      <div className="note">
        <bdi>{entry.body}</bdi>
      </div>
    </li>
  );
}

const MODE_LABELS: Record<ComposerMode, MessageId> = {
  reply: "cases.composer.reply",
  note: "cases.composer.note",
  call: "cases.composer.call",
};

const REPLY_BLOCKED: Record<NonNullable<CaseControls["replyBlocked"]>, MessageId> = {
  closed: "cases.reply.closed",
  unassigned: "cases.reply.unassigned",
  notOwner: "cases.reply.notOwner",
};

/** The modes this person can write in, in order. */
export function composerModes(controls: CaseControls): ComposerMode[] {
  return [
    ...(controls.reply ? (["reply"] as const) : []),
    ...(controls.note ? (["note"] as const) : []),
    ...(controls.logCall ? (["call"] as const) : []),
  ];
}

function Composer({ detail, controls, canReplyInApp, composer, onComposer, onReload }: CasePanelViewProps) {
  const modes = composerModes(controls);
  if (modes.length === 0) return null;
  const mode = modes.includes(composer.mode) ? composer.mode : modes[0]!;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    onComposer({ type: "submit" });
  };
  const noCustomer = mode === "reply" && !canReplyInApp;
  return (
    <form className={`composer composer-${mode}`} onSubmit={submit} noValidate>
      {controls.replyBlocked ? <p className="foot">{t(REPLY_BLOCKED[controls.replyBlocked])}</p> : null}
      <div className="tabs" role="tablist" aria-label={t("cases.composer.aria")}>
        {modes.map((each) => (
          <button
            key={each}
            type="button"
            role="tab"
            aria-selected={mode === each}
            className={`tab${mode === each ? " on" : ""}`}
            onClick={() => onComposer({ type: "mode", mode: each })}
          >
            {t(MODE_LABELS[each])}
          </button>
        ))}
      </div>
      {mode === "reply" ? (
        <p className="foot reply-hint">{noCustomer ? t("cases.reply.noChannel") : t("cases.reply.notified")}</p>
      ) : null}
      {mode === "note" ? <p className="foot internal-hint">{t("cases.note.hint")}</p> : null}
      {mode === "call" ? (
        <div className="field call-minutes">
          <label htmlFor="case-call-minutes">{t("cases.call.minutes")}</label>
          <input
            id="case-call-minutes"
            inputMode="numeric"
            value={composer.minutes}
            onChange={(event) => onComposer({ type: "minutes", minutes: event.target.value })}
          />
        </div>
      ) : null}
      <div className="field">
        <label htmlFor="case-composer">
          {mode === "reply"
            ? t("cases.reply.label", { name: detail.customer?.name || t("cases.unknownCaller") })
            : mode === "note"
              ? t("cases.note.label")
              : t("cases.call.label")}
        </label>
        <textarea
          id="case-composer"
          rows={3}
          value={composer.body}
          aria-invalid={composer.problem ? true : undefined}
          onChange={(event) => onComposer({ type: "body", body: event.target.value })}
        />
      </div>
      {composer.problem ? <p className="field-problem">{composer.problem}</p> : null}
      {composer.failure ? <CaseFailureNotice failure={composer.failure} onReload={onReload} /> : null}
      <div className="btns">
        <button className="btn primary" type="submit" disabled={composer.pending || noCustomer}>
          {composer.pending
            ? t("cases.sending")
            : mode === "reply"
              ? t("cases.reply.sendChat")
              : mode === "note"
                ? t("cases.note.submit")
                : t("cases.call.submit")}
        </button>
      </div>
    </form>
  );
}
